/**
 * @file Icons.tsx
 * @description Tiny shared stroke-icon set (16-20px) replacing the emoji
 *   glyphs previously used as button icons (⧉ ✉ 🖨 ⬇ ✎ 📄 …). Same
 *   inline-SVG pattern as HamburgerIcon in AppLayout: currentColor
 *   stroke, 1.75 width, aria-hidden. Pure presentation — callers pair
 *   each icon with a visible i18n label or aria-label.
 * @module Components/Common
 * @security Static markup only. No user input, no network.
 */

import type { JSX, ReactNode } from 'react';

interface IconProps {
  /** Tailwind size override; defaults to a 16px icon. */
  className?: string;
}

/** Shared SVG shell so every icon inherits the same stroke styling. */
function Icon({
  className = 'h-4 w-4',
  children,
}: IconProps & { children: ReactNode }): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function PencilIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <path d="M13.75 3.25l3 3L7.5 15.5l-3.75.75.75-3.75 9.25-9.25Z" />
    </Icon>
  );
}

export function DuplicateIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <rect x="7" y="7" width="9.5" height="9.5" rx="1.5" />
      <path d="M13 4.5A1.5 1.5 0 0 0 11.5 3h-6A1.5 1.5 0 0 0 4 4.5v6A1.5 1.5 0 0 0 5.5 12" />
    </Icon>
  );
}

export function MailIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <rect x="2.5" y="4.5" width="15" height="11" rx="1.5" />
      <path d="m3.5 6.5 6.5 4.5 6.5-4.5" />
    </Icon>
  );
}

export function PrinterIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <path d="M6 7V3h8v4" />
      <path d="M6 13.5H4.5A1.5 1.5 0 0 1 3 12v-3.5A1.5 1.5 0 0 1 4.5 7h11A1.5 1.5 0 0 1 17 8.5V12a1.5 1.5 0 0 1-1.5 1.5H14" />
      <rect x="6" y="11.5" width="8" height="5.5" />
    </Icon>
  );
}

export function DocumentIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <path d="M5 2.5h6l4 4v11H5v-15Z" />
      <path d="M11 2.5V6.5h4" />
    </Icon>
  );
}

export function DownloadIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <path d="M10 3v9m0 0 3.5-3.5M10 12 6.5 8.5" />
      <path d="M3.5 14.5v1A1.5 1.5 0 0 0 5 17h10a1.5 1.5 0 0 0 1.5-1.5v-1" />
    </Icon>
  );
}

export function XIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <path d="M5 5l10 10M15 5 5 15" />
    </Icon>
  );
}

export function ArchiveIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <rect x="3" y="3.5" width="14" height="3.5" rx="1" />
      <path d="M4.5 7v8A1.5 1.5 0 0 0 6 16.5h8a1.5 1.5 0 0 0 1.5-1.5V7" />
      <path d="M8 10.5h4" />
    </Icon>
  );
}

export function UnarchiveIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <rect x="3" y="3.5" width="14" height="3.5" rx="1" />
      <path d="M4.5 7v8A1.5 1.5 0 0 0 6 16.5h8a1.5 1.5 0 0 0 1.5-1.5V7" />
      <path d="M10 14V9.5m0 0-2 2m2-2 2 2" />
    </Icon>
  );
}

export function ShieldIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <path d="M10 2.5 4 5v4.5c0 4 2.7 6.9 6 8 3.3-1.1 6-4 6-8V5l-6-2.5Z" />
    </Icon>
  );
}

export function ChevronUpIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <path d="m5 12.5 5-5 5 5" />
    </Icon>
  );
}

export function ChevronDownIcon(p: IconProps): JSX.Element {
  return (
    <Icon {...p}>
      <path d="m5 7.5 5 5 5-5" />
    </Icon>
  );
}
